<script setup lang="ts">
import { inject, computed } from 'vue';
import { AboutContentRow } from '@/modules/content-blocks/interfaces/about-block.interface';
import { useJobBoardStore } from '@/core/collections/job-boards/job-board.store';

const props = defineProps({
   data: {
      type: Object,
      default: () => ({}),
   },
});
const viewport = inject<'desktop' | 'tablet' | 'mobile'>('viewport');

const rows = computed<AboutContentRow[]>(() => props.data.rows as AboutContentRow[]);

const jobBoardStore = useJobBoardStore();
const activeLang = computed(() => jobBoardStore.activeLanguageName);
</script>

<template>
   <section v-if="rows?.length" :class="['about-content', { 'about-content--mobile': viewport === 'mobile' }]">
      <div v-for="(row, idx) in rows" :key="idx" class="about-content__row" :class="{ 'about-content__row--reverse': !row.imageLeft }">
         <div v-if="row.image?.url" class="about-content__media">
            <img :src="row.image?.url" />
         </div>
         <div class="about-content__text">
            <h3>{{ row.title?.[activeLang] || '' }}</h3>
            <p v-html="row.description?.[activeLang] || ''"></p>
         </div>
      </div>
   </section>
   <section v-else>
      {{ $tb('live-editor.content-blocks.about.no-rows-configured') }}
   </section>
</template>

<style scoped lang="scss">
.about-content {
   display: flex;
   flex-direction: column;
   gap: var(--nxt-gutter-xlarge);
   max-width: 1000px;
   margin: 0 auto;

   &--mobile {
      .about-content__row {
         grid-template-columns: 1fr;
         direction: ltr;
      }

      .about-content__text {
         width: 80%;
         padding: 0;
         margin: 0 auto;
      }
   }

   @media (max-width: 400px) {
      .about-content__row {
         grid-template-columns: 1fr;
         direction: ltr;
      }

      .about-content__text {
         width: 80%;
         padding: 0;
         margin: 0 auto;
      }
   }

   &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      align-items: center;
      gap: var(--nxt-gutter-xlarge);

      &--reverse {
         direction: rtl;

         > * {
            direction: ltr;
         }
      }
   }

   &__media {
      img {
         width: 80%;
         height: auto;
         object-fit: cover;
         display: block;
         margin: 0 auto;
         border-radius: var(--jobsite-border-radius, var(--nxt-radius));
      }
   }

   &__text {
      padding: 50px;

      h3 {
         margin: 0;
         font-size: var(--nxt-font-xlarge);
         font-weight: var(--nxt-font-bold);
      }
   }
}
</style>
